import { HEADER_MOBILE_BREAKPOINT } from '@app/constants/breakpoints';
import { SCROLLING_DOWN_HEADER_WITH_BANNER_OFFSET } from '@app/constants/ui';
import {
  getCompanyName,
  getConfiguration,
  getIsLoggedIn,
  getIsMenuOpen,
  getIsSoftLogoutMode,
  getIsUserKnown,
  getUser,
  setIsMenuOpen,
} from '@app/ducks';
import { compiledPaths } from '@app/paths';
import { createAbsoluteUrl } from '@app/utils/createAbsoluteUrl';
import { getIsFeatureEnabled } from '@app/utils/getIsFeatureEnabled';
import { useIsScreenSize } from '@app/utils/hooks';
import { useIsScrollingDown } from '@app/utils/hooks/useIsScrollingDown';
import { Navigation } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Menu } from '../menu/Menu';
import { Button } from '../various/Button';

import { AccountDropdown } from './AccountDropdown';
import { LanguageSwitcher } from './LanguageSwitcher';
import { NavigationMenu } from './NavigationMenu';
import { WhatsNew } from './WhatsNew';

interface Props {
  navigationItems?: Navigation.Item[];
  suffix?: React.ReactNode;
  hasHeaderBanner?: boolean;
  shouldShowMenu?: boolean;
  logoUrl?: string;
}

export const Header = ({
  navigationItems = [],
  suffix,
  hasHeaderBanner,
  shouldShowMenu = true,
  logoUrl = compiledPaths.ROOT({}),
}: Props) => {
  const { t } = useTranslation(['common', 'login']);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isSoftLogout = useSelector(getIsSoftLogoutMode);
  const isMobile = useIsScreenSize(HEADER_MOBILE_BREAKPOINT);
  const config = useSelector(getConfiguration);
  const user = useSelector(getUser);
  const companyName = useSelector(getCompanyName);
  const isMenuOpen = useSelector(getIsMenuOpen);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isEnabledWhatsNew = getIsFeatureEnabled('ENABLE_WHATS_NEW');
  const offsetTop = hasHeaderBanner ? SCROLLING_DOWN_HEADER_WITH_BANNER_OFFSET : undefined;

  const isScrollingDown = useIsScrollingDown({ isEnabled: isMobile, offsetTop });

  const handleToggleMenu = (isOpen: boolean) => {
    dispatch(setIsMenuOpen(isOpen));
  };

  React.useEffect(() => {
    document.body.classList.toggle('scrollingDown', isMobile && isScrollingDown);
  }, [isMobile, isScrollingDown]);

  const softLoggedOutUserLabel = isMobile ? t('common:back') : t('common:back_to_my_showroom');
  const unloggedUserLabel = isMobile ? t('login:sign_in') : t('login:sign_in_to_showroom');

  const redirectUnloggedUser = React.useCallback(() => {
    navigate(
      { pathname: compiledPaths.LOGIN({}) },
      { state: { from: { pathname: window.location.pathname, search: window.location.search } } },
    );
  }, [navigate]);

  const redirectSoftLoggedOutUser = React.useCallback(() => {
    window.location.replace(createAbsoluteUrl(''));
  }, []);

  return (
    <>
      <div className="flex w-full items-center justify-between gap-6 px-4 py-2 lg:px-8 lg:py-4">
        <Menu
          isOpen={isMenuOpen}
          user={user}
          lookbookEnabled={config.lookbookEnabled}
          onOpenChange={handleToggleMenu}
          navigationItems={navigationItems}
          hasHeaderBanner={hasHeaderBanner}
          shouldShowMenu={shouldShowMenu}
          logoUrl={logoUrl}
        />
        {!isMobile && <NavigationMenu navigationItems={navigationItems} />}
        <div className="flex flex-1 shrink-0 items-center justify-end gap-1 lg:gap-4">
          {suffix}
          {isEnabledWhatsNew && <WhatsNew />}
          {getIsUserKnown(user) && isLoggedIn && <AccountDropdown user={user} companyName={companyName} />}
          {!isLoggedIn && <LanguageSwitcher />}
          {isSoftLogout && !isLoggedIn && (
            <Button onClick={redirectSoftLoggedOutUser} color="secondary" variant="bordered">
              {softLoggedOutUserLabel}
            </Button>
          )}
          {!isLoggedIn && !isSoftLogout && (
            <Button onClick={redirectUnloggedUser} color="secondary" variant="bordered">
              {unloggedUserLabel}
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
